import {useEffect, useState} from "react";
import LocalStorageService from "../../common/services/local-storage.service";
import {LocalStorageKeys} from "@common/models/enums/local-storage-keys.enum";
import NotificationService from "../../common/services/notification.service";
import useAuth from "../hooks/use-auth";

/**
 * Hook: useResetPasswordTokenHandler
 *
 * Manages password reset token actions with countdown timers:
 * - Handles resend tokens (once per minute, configurable).
 * - No automatic sending on mount (unlike email verification).
 *
 * Config:
 * - resendDelay (number): Delay in seconds before the resend token action is available. Default: 60 seconds.
 * - notifyOnResend (boolean): Determines if a notification should be displayed on token resend. Default: true.
 *
 * Features:
 * - Countdown persistence using LocalStorage.
 * - Notifications for success or failure.
 *
 * Returns:
 * - resendCountdown (number): Seconds until the resend action is available.
 * - handleResendToken (function): Triggers the resend token action.
 */
interface UseResetPasswordTokenHandlerConfig {
    resendDelay?: number;
    notifyOnResend?: boolean;
}

const defaultConfig: UseResetPasswordTokenHandlerConfig = {
    resendDelay: 5,
    notifyOnResend: true,
}

const useResetPasswordTokenHandler = (config?: UseResetPasswordTokenHandlerConfig) => {
    const {getTokenToResetPassword} = useAuth();

    const {
        resendDelay,
        notifyOnResend
    }: UseResetPasswordTokenHandlerConfig = {...defaultConfig, ...config};

    const getTimers = (): Record<string, number> => {
        const savedTimers = LocalStorageService.get(LocalStorageKeys.TIMER_MAP);
        return savedTimers ? savedTimers : {};
    };

    const setTimers = (timers: Record<string, number>): void => {
        LocalStorageService.save(LocalStorageKeys.TIMER_MAP, timers);
    };

    const [resendCountdown, setResendCountdown] = useState(() => {
        const timers: Record<string, number> = getTimers();
        return timers[`reset_password_resend`]
            ? Math.max(Number(timers[`reset_password_resend`]) - Math.floor(Date.now() / 1000), 0)
            : 0;
    });

    const handleResendToken = async (email: string) => {
        if (resendCountdown === 0) {
            try {
                const successMessage = notifyOnResend ? `Link do resetowania hasła został ponownie wysłany na ${email}` : undefined
                await getTokenToResetPassword(email, successMessage);
                setResendCountdown(resendDelay!);

                const timers: Record<string, number> = getTimers();
                timers[`reset_password_resend`] = Math.floor(Date.now() / 1000) + resendDelay!;
                setTimers(timers);
            } catch (error) {
                console.error("Failed to resend reset password token:", error);
                throw error;
            }
        }
    };

    useEffect(() => {
        let resendTimer: NodeJS.Timeout;
        if (resendCountdown > 0) {
            resendTimer = setInterval(() => setResendCountdown((prev) => prev - 1), 1000);
        }
        return () => clearInterval(resendTimer);
    }, [resendCountdown]);

    const setInitialTimer = () => {
        setResendCountdown(resendDelay!);
        const timers: Record<string, number> = getTimers();
        timers[`reset_password_resend`] = Math.floor(Date.now() / 1000) + resendDelay!;
        setTimers(timers);
    };

    return {
        resendCountdown,
        handleResendToken,
        setInitialTimer,
    };
};

export default useResetPasswordTokenHandler;
